import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, CheckCircle2, Target, Sparkles } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { fetchService, fetchTechTopics, fetchArticles } from "../lib/api";
import { Icon, SectionLoader } from "../components/shared";

export default function ServiceDetail() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [techMap, setTechMap] = useState({});
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound

  useEffect(() => {
    setStatus("loading");
    Promise.all([fetchService(slug), fetchTechTopics()])
      .then(async ([s, topics]) => {
        setService(s);
        setTechMap(Object.fromEntries(topics.map((t) => [t.slug, t])));
        if (s.related_article_slugs?.length) {
          const articles = await fetchArticles({ limit: 50 });
          setRelatedArticles(
            articles.filter((a) => s.related_article_slugs.includes(a.slug))
          );
        }
        setStatus("ok");
      })
      .catch(() => setStatus("notfound"));
  }, [slug]);

  if (status === "loading") {
    return (
      <div className="min-h-screen nst-light">
        <Navbar variant="solid" />
        <div className="pt-[76px]"><SectionLoader /></div>
      </div>
    );
  }
  if (status === "notfound" || !service) {
    return (
      <div className="min-h-screen nst-light">
        <Navbar variant="solid" />
        <div className="pt-[140px] px-6 text-center">
          <h1 className="text-3xl font-bold text-[#0A1F47]">Service not found</h1>
          <Link to="/#services" className="btn-navy mt-6">
            <ArrowLeft className="w-4 h-4" /> Back to Services
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#EDF1FB]">
      <Navbar variant="solid" />

      {/* Hero */}
      <section className="relative overflow-hidden nst-navy text-white pt-[140px] pb-24">
        <div className="absolute inset-0 bg-grid-dark opacity-30" />
        <div className="glow-orb w-[500px] h-[500px] -top-20 -right-20" style={{ background: "#2E6BF6" }} />

        <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10">
          <Link to="/#services" className="inline-flex items-center gap-2 text-sm font-semibold text-white/70 hover:text-white mb-8">
            <ArrowLeft className="w-4 h-4" /> All Services
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-8">
              <span className="pill-label on-dark"><Sparkles className="w-3.5 h-3.5" /> Service</span>
              <div className="flex items-center gap-5 mt-6 mb-6">
                <div className="w-16 h-16 rounded-2xl bg-[#2E6BF6]/15 border border-[#2E6BF6]/30 flex items-center justify-center">
                  <Icon name={service.icon} className="w-7 h-7 text-[#6B9FFF]" />
                </div>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05]">
                {service.title}
              </h1>
              <p className="mt-6 text-base md:text-lg text-white/75 max-w-2xl leading-relaxed">
                {service.long_desc || service.short_desc}
              </p>

              <div className="mt-8 flex flex-wrap gap-2.5">
                {service.related_tech_slugs?.map((ts) => {
                  const t = techMap[ts];
                  if (!t) return null;
                  return (
                    <Link key={ts} to={`/tech/${ts}`} className="tech-badge hover:border-white/60 hover:text-white transition-colors">
                      {t.name}
                    </Link>
                  );
                })}
              </div>
            </div>

            <div className="lg:col-span-4">
              <div className="bg-white/[0.05] border border-white/10 rounded-2xl p-7 backdrop-blur-sm">
                <div className="text-xs uppercase tracking-wider text-white/50 font-bold mb-4">What you get</div>
                <ul className="space-y-3">
                  {service.bullets?.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-sm text-white/90">
                      <CheckCircle2 className="w-5 h-5 text-[#6B9FFF] mt-0.5 flex-shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/#contact" className="btn-navy w-full justify-center mt-6" style={{ background: "#2E6BF6" }}>
                  Schedule Review <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Outcomes */}
      {service.outcomes?.length > 0 && (
        <section className="nst-light py-20">
          <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
            <span className="pill-label"><Target className="w-3.5 h-3.5" /> Outcomes</span>
            <h2 className="mt-6 text-3xl md:text-4xl font-extrabold text-[#0A1F47] leading-tight">
              Measurable <span className="text-[#2E6BF6]">business outcomes</span>
            </h2>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
              {service.outcomes.map((o) => (
                <div key={o} className="bg-white border border-[#E2E8F4] rounded-2xl p-6 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#EDF1FB] flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-5 h-5 text-[#2E6BF6]" />
                  </div>
                  <p className="text-[#0A1F47] font-medium">{o}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Sections */}
      {service.sections?.length > 0 && (
        <section className="nst-light pb-20">
          <div className="max-w-[1000px] mx-auto px-6 lg:px-10 space-y-10">
            {service.sections.map((sec, i) => (
              <div key={i} className="bg-white border border-[#E2E8F4] rounded-3xl p-8 md:p-10">
                {sec.heading && (
                  <h3 className="text-2xl md:text-3xl font-extrabold text-[#0A1F47]">{sec.heading}</h3>
                )}
                {sec.body && (
                  <p className="mt-4 text-[#0A1F47]/80 leading-relaxed text-[17px]">{sec.body}</p>
                )}
                {sec.bullets && (
                  <ul className="mt-5 space-y-2.5">
                    {sec.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-[#0A1F47]/80">
                        <CheckCircle2 className="w-[18px] h-[18px] text-[#2E6BF6] mt-1 flex-shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Related articles */}
      {relatedArticles.length > 0 && (
        <section className="nst-light py-20 border-t border-[#D9E0F0]">
          <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#0A1F47]">
              Related <span className="text-[#2E6BF6]">Insights</span>
            </h2>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
              {relatedArticles.map((a) => (
                <Link
                  key={a.slug}
                  to={`/insights/${a.slug}`}
                  className="group bg-white border border-[#E2E8F4] rounded-2xl p-6 card-lift hover:border-[#2E6BF6]/40"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <span className="text-xs font-semibold text-[#2E6BF6] uppercase tracking-wider">{a.category}</span>
                      <h3 className="text-lg font-bold text-[#0A1F47] mt-2 group-hover:text-[#2E6BF6] transition-colors">{a.title}</h3>
                      <p className="mt-2 text-sm text-[#0A1F47]/70">{a.excerpt}</p>
                    </div>
                    <ArrowUpRight className="w-5 h-5 text-[#0A1F47]/40 group-hover:text-[#2E6BF6]" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA strip */}
      <section className="nst-navy text-white py-16">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <h3 className="text-2xl md:text-3xl font-extrabold max-w-xl">
            Ready to discuss your {service.title.toLowerCase()} needs?
          </h3>
          <Link to="/#contact" className="btn-navy" style={{ background: "#2E6BF6" }}>
            Schedule Architecture Review <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
